import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Link, Stack, TableBody, TableContainer, IconButton as MuiIconButton, Skeleton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import {
  Button,
  CategorizationTag,
  Checkbox,
  CircularProgress,
  SnackbarProps,
  Tooltip,
  Typography,
  useSnackbar
} from '@chr/eds-react';
import { IconType } from '@chr/eds-react/Icon';

import { getDocumentTypeDisplayName, IsoToDateAtTime } from '@/common/utilities/display-utilities';
import { EmailDocumentModal } from './EmailDocumentModal';
import { Document } from '@/common/apis/dtos';
import { DocumentStatus } from '@/common/constants/document-types';
import useDocumentStore from '@/common/stores/document-store';
import { downloadDocuments } from '@/common/apis/documents';
import { downloadFile, previewFile } from '@/common/utilities/download-file';
import { DocumentActionBar } from './DocumentActionBar';
import { ProgressButton } from '@/common/components/progress-button/ProgressButton';
import { UploadDocumentModal } from './UploadDocumentModal';
import { EmptyContent } from '@/common/components/empty-content/EmptyContent';
import { StyledTable, StyledTableCell, StyledTableHead, StyledTableRow } from '@/common/components/table';
import { useAnalytics } from "@/common/hooks/useAnalytics";
import { PermissionsByPartyCode } from '@/common/constants/permission-types';
import { useChrLdFlag } from '@chr/common-launchdarkly';

export interface DocumentsProps {
  bookingNumber: number;
  loadNumbers: string[];
  permissions: PermissionsByPartyCode | undefined;
  customerPartyCode?: string;
}

export const Documents = ({ loadNumbers, bookingNumber, permissions, customerPartyCode }: DocumentsProps) => {
  const maxDocumentsDownload = 20;
  const queryClient = useQueryClient();
  const translate = useTranslation();
  const snackbar = useSnackbar();
  const { trackDocActivity } = useAnalytics();
  const { documents: documentStore } = useDocumentStore();

  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [documentsToEmail, setDocumentsToEmail] = useState<Document[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDocumentActionBar, setShowDocumentActionBar] = useState<boolean>(false);
  const [downloadingDocumentIds, setDownloadingDocumentIds] = useState<number[]>([]);
  const [showAddDocumentsModal, setShowAddDocumentsModal] = useState(false);
  const [showPreviewSpinner, setShowPreviewSpinner] = useState<Record<number, boolean | undefined>>({});

  const uploadedDocuments = documentStore?.filter(doc => doc.status === DocumentStatus.Uploaded) ?? [];
  const pendingDocuments = documentStore?.filter(doc => doc.status === DocumentStatus.InProgress) ?? [];

  const documentsPermissionLdFlag = useChrLdFlag('cp-document-permission-and-visibility');
  const hasDocumentPermission = documentsPermissionLdFlag && customerPartyCode 
    ? (permissions?.[customerPartyCode]?.hasViewOtherDocuments ?? false) 
    : true;

  const handleEmailDocuments = (documents: Document[]) => {
    setDocumentsToEmail(documents);
    setShowEmailModal(true);
  };

  const getEmptyContentText = () => {
    if (!hasDocumentPermission) {
      return 'There are no documents.';
    }
    return loadNumbers.length > 0 ? `To add a new document press the "Add Documents" button.` : `Document upload will activate once a shipment is assigned.`;
  };

  const getDownloadSnackbarProps = (success: boolean, numberOfDocs: number) => {
    let snackbarMessage: string;
    if (success) {
      snackbarMessage = numberOfDocs > 1 ?
        `${translate(`${numberOfDocs} documents downloaded successfully.`)}` :
        `${translate('Document download successful.')}`;
    } else {
      snackbarMessage = numberOfDocs > 1 ?
        `${translate(`${numberOfDocs} document downloads failed. Please try again later.`)}` :
        `${translate('Document download failed. Please try again later.')}`;
    }

    return {
      id: 'download-document-snackbar',
      message: snackbarMessage,
      severity: success ? 'positive' : 'negative'
    } as SnackbarProps;
  };

  const handleDownloadDocuments = async (documents: Document[]) => {
    setIsDownloading(true);

    // single file download
    if (documents.length == 1) {
      setDownloadingDocumentIds(downloadingDocumentIds.concat(documents[0]?.id as number));
    }

    try {
      // Measure the performance of the download activity
      const startTime = new Date().getTime();

      const response = await downloadDocuments(documents);
      downloadFile(response);

      trackDocActivity({
        numberOfDocuments: documents.length,
        typeOfDocument: documents?.map(doc => doc.description).join(', '),
        buttonText: 'Download',
        activityResult: 'Download',
        activityResponseTime: new Date().getTime() - startTime,
      });

      snackbar.enqueueSnackbar(getDownloadSnackbarProps(true, documents.length));
    } catch {
      snackbar.enqueueSnackbar(getDownloadSnackbarProps(false, documents.length));
    } finally {
      setIsDownloading(false);
      setDownloadingDocumentIds(downloadingDocumentIds.filter(x => x !== documents[0].id));
    }
  };

  const handlePreviewDocument = async (document: Document) => {
    // if document.fileType is not a pdf, png, jpeg, or bmp, download the file instead of previewing it
    if (!['pdf', 'png', 'jpeg', 'jpg', 'bmp'].includes(document.fileType?.toLowerCase() ?? '')) {
      handleDownloadDocuments([document]);
      return;
    }

    setShowPreviewSpinner({ [document.id!]: true });
    try {
      const response = await downloadDocuments([document]);
      previewFile(response.content, response.mimeType ?? 'application/octet-stream');
    } catch {
      snackbar.enqueueSnackbar(getDownloadSnackbarProps(false, 1));
    } finally {
      setShowPreviewSpinner({ [document.id!]: false });
    }
  };

  const closeActionBar = () => {
    setSelectedDocuments([]);
    setShowDocumentActionBar(false);
  };

  const selectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && uploadedDocuments.length > 0) {
      if(uploadedDocuments.length > maxDocumentsDownload){
        setSelectedDocuments(uploadedDocuments.slice(0, maxDocumentsDownload));
      }
      else{
        setSelectedDocuments(uploadedDocuments);
      }
      setShowDocumentActionBar(true);

      return;
    }

    setSelectedDocuments([]);
    setShowDocumentActionBar(false);
  };

  const selectDocument = (newSelectedDocument: Document) => {
    let newSelectedDocuments: Document[] = [];

    if (selectedDocuments.some(doc => doc.id === newSelectedDocument.id)) {
      newSelectedDocuments = selectedDocuments.filter(doc => doc.id !== newSelectedDocument.id);
    } else {
      newSelectedDocuments = selectedDocuments.concat(newSelectedDocument);
    }

    // This logic doesn't allow adding more documents if the maximum limit has been reached.
    if(newSelectedDocuments.length > maxDocumentsDownload){
      newSelectedDocuments = selectedDocuments;
    }

    setSelectedDocuments(newSelectedDocuments);

    const userAddedMoreDocuments = newSelectedDocuments.length > 1;
    setShowDocumentActionBar(userAddedMoreDocuments);
  };

  const isSelectedDocument = (document: Document): boolean => selectedDocuments.indexOf(document) !== -1;
  const isFirstDocumentSelected = (): boolean => selectedDocuments.indexOf(uploadedDocuments[0]) >= 0;

  const renderAddDocumentsButton = () => {
    if (!hasDocumentPermission) {
      return null;
    }

    return (
      <>
        {(loadNumbers.length < 1) &&
          <Tooltip title={translate('Document upload disabled until booking is assigned to a shipment.')}>
            <MuiIconButton>
              <InfoOutlinedIcon
                fontSize="large"
              />
            </MuiIconButton>
          </Tooltip>
        }
        <Button
          variant="primary"
          aria-label="Add Documents"
          startIcon={'attach_file' as IconType}
          buttonText={translate('Add Documents')}
          onClick={() => setShowAddDocumentsModal(true)}
          disabled={loadNumbers.length < 1}
        />
      </>
    )
  }

  const renderDocumentContent = () => {
    if (uploadedDocuments && uploadedDocuments.length > 0) {
      return (
        <>
          {showDocumentActionBar &&
            <DocumentActionBar
              isDownloading={isDownloading}
              documentCount={uploadedDocuments.length}
              selectedDocuments={selectedDocuments}
              showMaxSelected={selectedDocuments.length >= maxDocumentsDownload}
              closeActionBar={closeActionBar}
              handleEmailDocuments={handleEmailDocuments}
              handleDownloadDocuments={handleDownloadDocuments}
            />
          }
          <TableContainer
            sx={{
              marginTop: showDocumentActionBar ? '4px !important' : '32px',
              '& input[type=checkbox]': {
                position: 'absolute'
              }
            }}
          >
            <Box sx={{ borderRadius: '4px', border: '1px solid #E5E6E7' }}>
              <StyledTable aria-label="Documents Table">
                <StyledTableHead>
                  <StyledTableRow sx={{
                    ...(isFirstDocumentSelected() && {
                      borderBottom: 'none !important'
                    })
                  }}>
                    <StyledTableCell padding="checkbox" sx={{ padding: '0 14px' }}>
                      <Checkbox
                        checkedValue={uploadedDocuments.length > 0 && selectedDocuments.length === uploadedDocuments.length}
                        aria-label="Select All Documents"
                        onChange={selectAll}
                        data-testid="select-all-documents-checkbox"
                      />
                    </StyledTableCell>
                    <StyledTableCell>{translate('Document Name')}</StyledTableCell>
                    <StyledTableCell>{translate('Document Type')}</StyledTableCell>
                    <StyledTableCell>{translate('File Type')}</StyledTableCell>
                    <StyledTableCell>{translate('Shipment Number')}</StyledTableCell>
                    <StyledTableCell>{translate('Added On')}</StyledTableCell>
                    <StyledTableCell className="no-print">{translate('Sharing')}</StyledTableCell>
                    <StyledTableCell />
                  </StyledTableRow>
                </StyledTableHead>
                <TableBody>
                  {uploadedDocuments.map((document, index) => {
                    const isDocumentSelected = isSelectedDocument(document as Document);

                    return (
                      <StyledTableRow
                        key={document.id}
                        sx={{
                          ...(isDocumentSelected && {
                            backgroundColor: isDocumentSelected ? '#0071C714 !important' : 'inherit',
                            borderTop: '1px solid #0071C7',
                            borderBottom: '1px solid #0071C7'
                          })
                        }}
                      >
                        <StyledTableCell padding="checkbox" sx={{ padding: '0 14px' }}
                          data-testid={`select-document-cell-${index}`}>
                          <Checkbox
                            checkedValue={isDocumentSelected}
                            aria-label={`Select Document ${document.name}`}
                            onChange={() => selectDocument(document as Document)}
                          />
                        </StyledTableCell>
                        <StyledTableCell sx={{ paddingTop: 0, paddingBottom: 0 }}
                          data-testid={`open-document-link-${document.id}`}>
                          <Link
                            component="button"
                            variant="body1"
                            aria-label="Click to open document"
                            onClick={() => handlePreviewDocument(document)}
                          >
                            <Stack gap={2} direction="row">
                              {document.name ?? '-'}
                              {(showPreviewSpinner[document.id!]) && <CircularProgress size="md" />}
                            </Stack>
                          </Link>
                        </StyledTableCell>
                        <StyledTableCell>{translate(document.description ? document.description : "")}</StyledTableCell>
                        <StyledTableCell>.{document.fileType?.toUpperCase()}</StyledTableCell>
                        <StyledTableCell>{document.loadNumber}</StyledTableCell>
                        <StyledTableCell
                          className="no-print">{IsoToDateAtTime(document.createdDate, translate)}</StyledTableCell>
                        <StyledTableCell padding="checkbox" sx={{ paddingTop: 0, paddingBottom: 0 }}>
                          <Box marginRight="5px">
                            <Button
                              variant="tertiary"
                              aria-label="Email Documents"
                              startIcon={'email' as IconType}
                              buttonText={translate('Email')}
                              onClick={() => handleEmailDocuments([document])}
                            />
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell padding="checkbox"
                          sx={{ paddingTop: 0, paddingBottom: 0, marginRight: '9px' }}
                          className="no-print">
                          <Box marginRight="5px">
                            <ProgressButton
                              variant="tertiary"
                              aria-label="Download Documents"
                              startIcon="download"
                              buttonText={translate('Download')}
                              onClick={() => handleDownloadDocuments([document])}
                              showProgress={downloadingDocumentIds.includes(document.id || 0)}
                            />
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                  {pendingDocuments.map((document) => {
                    return (
                      <StyledTableRow key={`pending-file-${document.id}`}>
                        <StyledTableCell padding="checkbox">
                          <Skeleton variant="rectangular" height={'24px'} width={'24px'}
                            sx={{ marginLeft: '6px' }} />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Skeleton variant="rectangular" height={'24px'} />
                        </StyledTableCell>
                        <StyledTableCell>
                          {translate(getDocumentTypeDisplayName(document.type))}
                        </StyledTableCell>
                        <StyledTableCell>
                          {document.fileType?.toUpperCase()}
                        </StyledTableCell>
                        <StyledTableCell>
                          {document.loadNumber}
                        </StyledTableCell>
                        <StyledTableCell>
                          <CategorizationTag
                            label={translate('Upload in progress')}
                          ></CategorizationTag>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Skeleton variant="rectangular" height={'24px'} />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Skeleton variant="rectangular" height={'24px'} />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </StyledTable>
            </Box>
          </TableContainer>
        </>
      )
    }

    return (
      <EmptyContent
        title={translate('No Documents Found')}
        bodyText={translate(getEmptyContentText())}
      />
    );
  }

  return (
    <>
      <Stack spacing={4} padding={1} paddingBottom={4} data-testid="booking-documents-tab">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Stack spacing={1}>
            <Typography variant="display300">{translate('Documents')}</Typography>
            <Typography variant="body100">
              {translate('Access all documents associated with your booking. Upload additional documents once a carrier has been booked.')}
            </Typography>
          </Stack>

          <Stack
            minWidth={'220px'}
            direction={'row'}
            spacing={1}
          >
            {pendingDocuments.length > 0 &&
              <Button
                variant="secondary"
                aria-label="refresh documents table"
                startIcon={'cached' as IconType}
                buttonText={translate('Refresh Table')}
                onClick={() => queryClient.invalidateQueries(['documents', bookingNumber])}
              />
            }
            {renderAddDocumentsButton()}
          </Stack>
        </Stack>
        {renderDocumentContent()}
      </Stack>
      <EmailDocumentModal
        open={showEmailModal}
        handleClose={() => {
          setShowEmailModal(false);
        }}
        documents={documentsToEmail} />
      <UploadDocumentModal
        openModal={showAddDocumentsModal}
        handleClose={() => {
          setShowAddDocumentsModal(false);
        }}
        shipmentIds={loadNumbers}
        bookingNumber={bookingNumber}
      />
    </>
  );
};
